/**
 * posPrinterConfig.ts — Configuración de impresora térmica / gaveta (POS).
 *
 * QUÉ HACE:
 *  - Modela la configuración de la impresora térmica (transport, papel, marca,
 *    red/IP, Bluetooth/MAC) y las preferencias de comportamiento (recibos,
 *    impresión automática, apertura automática de gaveta).
 *  - Persiste en localStorage y, en APK, también en SharedPreferences vía AltenotPos.
 *
 * QUÉ EXPORTA:
 *  - Tipos: PosPrinterTransport, PosPrinterConfig
 *  - Defaults: DEFAULT_POS_PRINTER_CONFIG
 *  - Lectura/escritura: loadPosPrinterConfig, persistPosPrinterConfig,
 *    loadPosPrinterConfigFromStorage, resetPosPrinterConfig, clearPosPrinterConfigStorage
 *  - Helpers: getEffectivePrinterAddress, isPosPrinterConfigured,
 *    resolvePrinterConfig (tickets/labels), isLabelPrinterConfigured,
 *    shouldAutoPrintOnSale, isDrawerTestAllowed,
 *    markDrawerTestUsed
 *
 * QUÉ NO DEBE HACER:
 *  - No conoce de UI ni de React.
 *  - No envía las preferencias `printReceipts`, `autoPrintOnSale`,
 *    `drawerTestUsed` al nativo (son del frontend).
 *
 * REGLAS:
 *  - Las preferencias nuevas son OPT-IN compatibles con configuraciones antiguas
 *    (ver normalizeParsed). NUNCA cambiar defaults sin migrar.
 *  - `drawerTestUsed` se marca tras una prueba exitosa y se libera SOLO al hacer
 *    reset total (resetPosPrinterConfig). Es la barrera anti-fraude para que el
 *    botón «Probar gaveta» no quede al alcance del personal en operación.
 */

import { isNative } from "@/lib/platform";
import { AltenotPos, type PosPrinterConfigPayload } from "./altenotPos";
import { POS_PRINTER_BRANDS } from "@/features/caja/constants/posPrinterCatalog";
import type { PosPrinterBrandValue } from "@/features/caja/constants/posPrinterCatalog";
import {
  txReadRaw,
  txWriteRaw,
  txRemove,
} from "@/lib/persistence/transactionalStore";

const KNOWN_BRANDS = new Set<string>(
  POS_PRINTER_BRANDS.map((b) => b.value as string),
);

const STORAGE_KEY = "altenot_pos_printer_v2";

/** IP de ejemplo para prueba rápida en red local (cada impresora puede tener otra). */
export const POS_DEFAULT_NETWORK_HOST = "192.168.1.100";

export type PosPrinterTransport = "none" | "bluetooth_spp" | "network" | "usb";

/** Modo de la impresora de etiquetas de pesaje. */
export type LabelPrinterMode = "same" | "separate" | "off";

/**
 * Configuración de la impresora de ETIQUETAS de pesaje (opcional).
 * Solo se usa cuando `labelPrinterMode === "separate"`.
 */
export interface PosLabelPrinterConfig {
  transport: PosPrinterTransport;
  paperWidthMm: 58 | 80;
  networkHost: string;
  networkPort: number;
  networkRelayUrl: string;
  bluetoothMac: string;
  usbDeviceKey: string;
}

export interface PosPrinterConfig {
  transport: PosPrinterTransport;
  paperWidthMm: 58 | 80;
  brand: PosPrinterBrandValue | string;
  deviceName: string;
  networkHost: string;
  networkPort: number;
  /**
   * URL de relay local para impresión Wi-Fi desde el navegador web.
   * El navegador no puede abrir sockets TCP directamente; un pequeño servidor
   * relay (ej. Node.js en el mismo PC) escucha peticiones HTTP y las reenvía
   * al socket TCP de la impresora.
   * Ejemplo: "http://localhost:8765"
   * Solo aplica cuando platform === "web" y transport === "network".
   */
  networkRelayUrl: string;
  bluetoothMac: string;
  /** Clave USB OTG: vendorId:productId (ej. 1234:5678). */
  usbDeviceKey: string;
  /** Compat: dirección compuesta guardada en versiones anteriores */
  address?: string;
  // ─── Preferencias de comportamiento (sólo frontend) ────────────────────────
  /** Master switch: si está OFF, no se muestra ni se imprime ningún recibo de venta. */
  printReceipts: boolean;
  /** Si está ON, tras una venta el ticket se imprime automáticamente sin pedir confirmación. */
  autoPrintOnSale: boolean;
  /** Si está ON, tras cobrar se muestra «Imprimir ticket» / vista previa (puede ir con auto). */
  manualReceiptOnSale: boolean;
  /** Anti-fraude: una vez probada la gaveta desde Preferencias, el botón se bloquea
   *  hasta hacer reset total. Evita que un empleado abra la caja desde Ajustes. */
  drawerTestUsed: boolean;
  // ─── Impresora de etiquetas de pesaje (carnicería / venta por peso) ────────
  /** "same" = etiqueta en la impresora de recibos; "separate" = segunda impresora;
   *  "off" = botón «Imprimir etiqueta» oculto en el modal de peso. */
  labelPrinterMode: LabelPrinterMode;
  /** Campos de conexión de la segunda impresora (solo si mode === "separate"). */
  labelPrinter: PosLabelPrinterConfig;
}

export const DEFAULT_LABEL_PRINTER_CONFIG: PosLabelPrinterConfig = {
  transport: "none",
  paperWidthMm: 58,
  networkHost: "",
  networkPort: 9100,
  networkRelayUrl: "",
  bluetoothMac: "",
  usbDeviceKey: "",
};

export const DEFAULT_POS_PRINTER_CONFIG: PosPrinterConfig = {
  transport: "none",
  paperWidthMm: 58,
  brand: "generic",
  deviceName: "",
  networkHost: "",
  networkPort: 9100,
  networkRelayUrl: "",
  bluetoothMac: "",
  usbDeviceKey: "",
  printReceipts: true,
  autoPrintOnSale: true,
  manualReceiptOnSale: true,
  drawerTestUsed: false,
  labelPrinterMode: "same",
  labelPrinter: { ...DEFAULT_LABEL_PRINTER_CONFIG },
};

function normalizeParsed(p: Partial<PosPrinterConfig>): PosPrinterConfig {
  const transport =
    p.transport === "bluetooth_spp" ||
    p.transport === "network" ||
    p.transport === "usb"
      ? p.transport
      : "none";
  const paper =
    p.paperWidthMm === 80 || p.paperWidthMm === 58 ? p.paperWidthMm : 58;
  let networkHost =
    typeof p.networkHost === "string" ? p.networkHost.trim() : "";
  let networkPort =
    typeof p.networkPort === "number" && Number.isFinite(p.networkPort)
      ? Math.min(65535, Math.max(1, Math.round(p.networkPort)))
      : 9100;
  let bluetoothMac =
    typeof p.bluetoothMac === "string" ? p.bluetoothMac.trim() : "";
  let usbDeviceKey =
    typeof p.usbDeviceKey === "string" ? p.usbDeviceKey.trim() : "";

  // Migración v1: solo `address` + transport
  const legacyAddr = typeof p.address === "string" ? p.address.trim() : "";
  if (!usbDeviceKey && transport === "usb" && legacyAddr) {
    usbDeviceKey = legacyAddr;
  }
  if (!networkHost && transport === "network" && legacyAddr) {
    const idx = legacyAddr.lastIndexOf(":");
    if (idx > 0) {
      const maybePort = parseInt(legacyAddr.slice(idx + 1), 10);
      if (!Number.isNaN(maybePort) && maybePort > 0) {
        networkHost = legacyAddr.slice(0, idx).trim();
        networkPort = maybePort;
      } else {
        networkHost = legacyAddr;
      }
    } else {
      networkHost = legacyAddr;
    }
  }
  if (!bluetoothMac && transport === "bluetooth_spp" && legacyAddr) {
    bluetoothMac = legacyAddr.replace(/\s/g, "").toUpperCase();
  }

  const asBool = (v: unknown, fallback: boolean): boolean =>
    typeof v === "boolean" ? v : fallback;

  // Impresora de etiquetas: opt-in, nunca rompe configs existentes.
  const labelMode: LabelPrinterMode =
    p.labelPrinterMode === "separate" || p.labelPrinterMode === "off"
      ? p.labelPrinterMode
      : "same";
  const lp: Partial<PosLabelPrinterConfig> = p.labelPrinter ?? {};
  const lpTransport =
    lp.transport === "bluetooth_spp" ||
    lp.transport === "network" ||
    lp.transport === "usb"
      ? lp.transport
      : "none";

  return {
    transport,
    paperWidthMm: paper,
    brand:
      typeof p.brand === "string" && p.brand && KNOWN_BRANDS.has(p.brand)
        ? (p.brand as PosPrinterBrandValue)
        : "generic",
    deviceName: typeof p.deviceName === "string" ? p.deviceName : "",
    networkHost,
    networkPort,
    networkRelayUrl:
      typeof p.networkRelayUrl === "string" ? p.networkRelayUrl.trim() : "",
    bluetoothMac,
    usbDeviceKey,
    printReceipts: asBool(
      p.printReceipts,
      DEFAULT_POS_PRINTER_CONFIG.printReceipts,
    ),
    autoPrintOnSale: asBool(
      p.autoPrintOnSale,
      DEFAULT_POS_PRINTER_CONFIG.autoPrintOnSale,
    ),
    manualReceiptOnSale: asBool(
      p.manualReceiptOnSale,
      DEFAULT_POS_PRINTER_CONFIG.manualReceiptOnSale,
    ),
    drawerTestUsed: asBool(
      p.drawerTestUsed,
      DEFAULT_POS_PRINTER_CONFIG.drawerTestUsed,
    ),
    labelPrinterMode: labelMode,
    labelPrinter: {
      transport: lpTransport,
      paperWidthMm:
        lp.paperWidthMm === 80 || lp.paperWidthMm === 58 ? lp.paperWidthMm : 58,
      networkHost:
        typeof lp.networkHost === "string" ? lp.networkHost.trim() : "",
      networkPort:
        typeof lp.networkPort === "number" && Number.isFinite(lp.networkPort)
          ? Math.min(65535, Math.max(1, Math.round(lp.networkPort)))
          : 9100,
      networkRelayUrl:
        typeof lp.networkRelayUrl === "string" ? lp.networkRelayUrl.trim() : "",
      bluetoothMac:
        typeof lp.bluetoothMac === "string" ? lp.bluetoothMac.trim() : "",
      usbDeviceKey:
        typeof lp.usbDeviceKey === "string" ? lp.usbDeviceKey.trim() : "",
    },
  };
}

export function loadPosPrinterConfigFromStorage(): PosPrinterConfig {
  try {
    const raw = txReadRaw(STORAGE_KEY);
    if (!raw) {
      const legacy = txReadRaw("altenot_pos_printer_v1");
      if (legacy) {
        const o = JSON.parse(legacy) as Partial<PosPrinterConfig>;
        const n = normalizeParsed(o);
        savePosPrinterConfigToStorage(n);
        return n;
      }
      return { ...DEFAULT_POS_PRINTER_CONFIG };
    }
    return normalizeParsed(JSON.parse(raw) as Partial<PosPrinterConfig>);
  } catch {
    return { ...DEFAULT_POS_PRINTER_CONFIG };
  }
}

function savePosPrinterConfigToStorage(c: PosPrinterConfig): void {
  const clean = normalizeParsed(c);
  txWriteRaw(STORAGE_KEY, JSON.stringify(clean));
}

/** Dirección efectiva para el plugin nativo (legado `address`). */
export function getEffectivePrinterAddress(c: PosPrinterConfig): string {
  const n = normalizeParsed(c);
  if (n.transport === "network" && n.networkHost)
    return `${n.networkHost}:${n.networkPort}`;
  if (n.transport === "bluetooth_spp" && n.bluetoothMac) return n.bluetoothMac;
  if (n.transport === "usb" && n.usbDeviceKey) return n.usbDeviceKey;
  return "";
}

export function isPosPrinterConfigured(c: PosPrinterConfig): boolean {
  const n = normalizeParsed(c);
  if (n.transport === "none") return false;
  if (n.transport === "usb") return n.usbDeviceKey.length > 0;
  if (n.transport === "network") return n.networkHost.length > 0;
  return n.bluetoothMac.length > 0;
}

/**
 * Resuelve la config efectiva para un destino de impresión.
 * · "tickets" → siempre la impresora principal.
 * · "labels" → la de etiquetas si `labelPrinterMode === "separate"`;
 *   si es "same" devuelve la principal (imprimir en la misma térmica).
 * Cuando la etiqueta está "off", devuelve la principal con transport "none"
 * (isPosPrinterConfigured dará false) para que el caller bloquee la impresión.
 */
export function resolvePrinterConfig(
  c: PosPrinterConfig,
  target: "tickets" | "labels",
): PosPrinterConfig {
  const n = normalizeParsed(c);
  if (target === "labels") {
    if (n.labelPrinterMode === "off") {
      return normalizeParsed({ ...n, transport: "none" });
    }
    if (n.labelPrinterMode === "separate") {
      const lp = n.labelPrinter;
      return normalizeParsed({
        ...n,
        transport: lp.transport,
        paperWidthMm: lp.paperWidthMm,
        networkHost: lp.networkHost,
        networkPort: lp.networkPort,
        networkRelayUrl: lp.networkRelayUrl,
        bluetoothMac: lp.bluetoothMac,
        usbDeviceKey: lp.usbDeviceKey,
      });
    }
  }
  return n;
}

/**
 * Indica si la impresora de etiquetas está lista para imprimir:
 * · "off" → false (botón oculto).
 * · "separate" → la segunda impresora está configurada.
 * · "same" → la impresora principal está configurada.
 */
export function isLabelPrinterConfigured(c: PosPrinterConfig): boolean {
  const n = normalizeParsed(c);
  if (n.labelPrinterMode === "off") return false;
  return isPosPrinterConfigured(resolvePrinterConfig(n, "labels"));
}

function mapFromPlugin(p: PosPrinterConfigPayload | null | undefined) {
  if (!p) return { ...DEFAULT_POS_PRINTER_CONFIG };
  const portRaw = p.networkPort;
  const port =
    typeof portRaw === "number"
      ? portRaw
      : typeof portRaw === "string"
        ? parseInt(portRaw, 10)
        : 9100;
  const withAddr = p as PosPrinterConfigPayload & { address?: string };
  return normalizeParsed({
    transport: p.transport as PosPrinterConfig["transport"],
    paperWidthMm: p.paperWidthMm === 80 ? 80 : 58,
    brand: typeof p.brand === "string" ? p.brand : "generic",
    deviceName: typeof p.deviceName === "string" ? p.deviceName : "",
    networkHost: typeof p.networkHost === "string" ? p.networkHost : "",
    networkPort: Number.isFinite(port) ? port : 9100,
    bluetoothMac: typeof p.bluetoothMac === "string" ? p.bluetoothMac : "",
    usbDeviceKey:
      typeof (p as PosPrinterConfigPayload & { usbDeviceKey?: string })
        .usbDeviceKey === "string"
        ? ((p as PosPrinterConfigPayload & { usbDeviceKey?: string })
            .usbDeviceKey ?? "")
        : "",
    address: typeof withAddr.address === "string" ? withAddr.address : "",
  });
}

function mergeHardwareWithLocalBehavior(
  hardware: PosPrinterConfig,
  local: PosPrinterConfig,
): PosPrinterConfig {
  return normalizeParsed({
    ...hardware,
    printReceipts: local.printReceipts,
    autoPrintOnSale: local.autoPrintOnSale,
    manualReceiptOnSale: local.manualReceiptOnSale,
    drawerTestUsed: local.drawerTestUsed,
  });
}

export async function loadPosPrinterConfig(): Promise<PosPrinterConfig> {
  const local = loadPosPrinterConfigFromStorage();
  if (!isNative) return local;
  try {
    const remote = await AltenotPos.getPrinterConfig();
    const mapped = mapFromPlugin(remote);
    if (
      mapped.transport !== "none" ||
      mapped.networkHost ||
      mapped.bluetoothMac
    ) {
      const merged = mergeHardwareWithLocalBehavior(mapped, local);
      savePosPrinterConfigToStorage(merged);
      return merged;
    }
  } catch {
    /* local */
  }
  return local;
}

export async function persistPosPrinterConfig(
  c: PosPrinterConfig,
): Promise<void> {
  const n = normalizeParsed(c);
  savePosPrinterConfigToStorage(n);
  if (!isNative) return;
  try {
    await AltenotPos.setPrinterConfig({
      transport: n.transport,
      address: getEffectivePrinterAddress(n),
      deviceName: n.deviceName.trim(),
      networkHost: n.networkHost.trim(),
      networkPort: n.networkPort,
      bluetoothMac: n.bluetoothMac.trim().toUpperCase(),
      usbDeviceKey: n.usbDeviceKey.trim(),
      paperWidthMm: n.paperWidthMm,
      brand: String(n.brand),
    });
  } catch {
    /* LS ok */
  }
}

export function clearPosPrinterConfigStorage(): void {
  txRemove(STORAGE_KEY);
  txRemove("altenot_pos_printer_v1");
}

// ─── Helpers de comportamiento (UI / flujo de venta) ────────────────────────

/**
 * Indica si la app debe imprimir el recibo automáticamente al confirmar la venta.
 * Requiere: recibos ON + auto ON. El intento de impresión (térmica o fallback)
 * lo resuelve PaymentView / posHardware aunque la térmica aún no esté enlazada.
 */
export function shouldAutoPrintOnSale(c: PosPrinterConfig): boolean {
  if (!c.printReceipts) return false;
  return c.autoPrintOnSale;
}

/** Botones «Imprimir ticket» / vista previa tras cobrar. */
export function shouldShowManualReceipt(c: PosPrinterConfig): boolean {
  if (!c.printReceipts) return false;
  return c.manualReceiptOnSale;
}

/**
 * Política anti-fraude del botón «Probar gaveta»:
 *  - Sólo se permite UNA prueba por configuración.
 *  - Tras una prueba exitosa (o tras guardar con la marca puesta), el botón se
 *    bloquea hasta hacer reset total. Así un empleado no puede abrir la caja
 *    desde Ajustes durante la operación diaria.
 */
export function isDrawerTestAllowed(c: PosPrinterConfig): boolean {
  if (c.drawerTestUsed) return false;
  return isPosPrinterConfigured(c);
}

/**
 * Marca la prueba de gaveta como consumida y persiste el cambio.
 * Devuelve la nueva configuración para que el caller la propague al estado.
 */
export async function markDrawerTestUsed(
  c: PosPrinterConfig,
): Promise<PosPrinterConfig> {
  if (c.drawerTestUsed) return c;
  const next: PosPrinterConfig = { ...c, drawerTestUsed: true };
  await persistPosPrinterConfig(next);
  return next;
}

/** Borra configuración local y en Android (prefs del plugin). */
export async function resetPosPrinterConfig(): Promise<void> {
  clearPosPrinterConfigStorage();
  if (!isNative) return;
  try {
    await AltenotPos.setPrinterConfig({
      transport: "none",
      address: "",
      deviceName: "",
      networkHost: "",
      networkPort: 9100,
      bluetoothMac: "",
      paperWidthMm: 58,
      brand: "generic",
    });
  } catch {
    /* ok */
  }
}
